"use client";

import { useState, useRef, useEffect } from "react";
import { Icon } from "@/components/ui/Icon";
import { motion, AnimatePresence } from "framer-motion";
import {
  format,
  addMinutes,
  isBefore,
  isAfter,
  setHours,
  setMinutes,
} from "date-fns";
import { TimePickerProps } from "./types";
import { tr } from "@/config/localeText";
import { RESERVATION_UI } from "./reservationFormUi";

export const TimePicker = ({
  value,
  onChange,
  label,
  id,
  selectedDate,
  onSelectNow,
  locale = "fr",
}: TimePickerProps) => {
  const t = tr(RESERVATION_UI, locale).timePicker;
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const isNowSelected = value?.startsWith("now:") ?? false;

  const [selectedHour, selectedMinute] = value ? value.split(":") : ["", ""];

  // Générer les heures et minutes
  const hours = Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );
  const minutes = Array.from({ length: 12 }, (_, i) =>
    (i * 5).toString().padStart(2, "0"),
  );

  const isTimeDisabled = (hour: string, minute: string) => {
    if (!selectedDate) return false;

    const now = new Date();
    const selectedDateTime = new Date(selectedDate);
    selectedDateTime.setHours(parseInt(hour));
    selectedDateTime.setMinutes(parseInt(minute));

    // Si la date sélectionnée est aujourd'hui
    if (selectedDateTime.toDateString() === now.toDateString()) {
      // Désactiver les heures passées
      if (isBefore(selectedDateTime, now)) return true;

      // Désactiver les heures trop proches (moins de 30 minutes)
      const minTime = addMinutes(now, 30);
      if (isBefore(selectedDateTime, minTime)) return true;
    }

    return false;
  };

  const handleNow = () => {
    const now = new Date();
    const roundedMinutes = Math.ceil(now.getMinutes() / 5) * 5;
    const adjustedTime = addMinutes(
      now,
      roundedMinutes - now.getMinutes() + 30,
    );
    onChange(`now:${format(adjustedTime, "HH:mm")}`);
    onSelectNow?.();
    setIsOpen(false);
    setIsFocused(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTimeSelect = (hour: string, minute: string) => {
    if (isTimeDisabled(hour, minute)) return;
    onChange(`${hour}:${minute}`);
    setIsOpen(false);
    setIsFocused(false);
  };

  const handleInputClick = () => {
    setIsOpen(true);
    setIsFocused(true);
  };

  return (
    <div className="relative w-full px-2 md:w-1/2" ref={pickerRef}>
      <label htmlFor={id} className="mb-2 block font-medium text-black">
        {label}
      </label>
      <div className="relative">
        <div
          className="absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 cursor-pointer text-gold-700"
          onClick={handleInputClick}
        >
          <Icon
            icon={isNowSelected ? "ph:clock-countdown" : "ph:clock"}
            className="h-5 w-5"
          />
        </div>
        <input
          type="text"
          id={id}
          value={
            value?.startsWith("now:") &&
            selectedDate === format(new Date(), "yyyy-MM-dd")
              ? t.now
              : value?.startsWith("now:")
                ? value.replace("now:", "")
                : value || ""
          }
          readOnly
          onClick={handleInputClick}
          className="h-12 w-full cursor-pointer rounded-lg border border-black/15 bg-white py-3 pe-4 ps-10 text-black outline-none transition-all placeholder:text-black/40 focus:border-gold-600 focus:ring-1 focus:ring-gold-300"
          placeholder={t.selectTime}
        />

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="absolute z-[9999] mt-2 flex max-h-[300px] w-[320px] flex-col rounded-lg border border-black/15 bg-white shadow-[0_10px_28px_rgba(0,0,0,0.12)]"
            >
              <button
                onClick={handleNow}
                className="flex items-center gap-2 border-b border-black/15 p-3 text-sm text-black transition-colors hover:bg-black/5"
              >
                <Icon
                  icon="ph:clock-countdown"
                  className="h-4 w-4 text-gold-700"
                />
                <span>{t.now}</span>
              </button>

              <div className="flex flex-1 overflow-hidden">
                {/* Heures */}
                <div className="flex-1 overflow-y-auto border-e border-black/15 p-2">
                  <div className="text-center text-xs font-medium text-black/60">
                    {t.hours}
                  </div>
                  <div className="mt-2 space-y-1">
                    {hours.map((hour) => {
                      const isDisabled = isTimeDisabled(
                        hour,
                        selectedMinute || "00",
                      );
                      return (
                        <button
                          key={hour}
                          onClick={() =>
                            handleTimeSelect(hour, selectedMinute || "00")
                          }
                          disabled={isDisabled}
                          className={`flex h-8 w-full items-center justify-center rounded-lg text-sm transition-colors ${
                            hour === selectedHour
                              ? "bg-primary font-medium text-black"
                              : isDisabled
                                ? "cursor-not-allowed bg-black/5 text-black/30"
                                : "text-black hover:bg-black/10"
                          }`}
                          title={t.hoursTitle(hour)}
                        >
                          {hour}h
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Minutes */}
                <div className="flex-1 overflow-y-auto p-2">
                  <div className="text-center text-xs font-medium text-black/60">
                    {t.minutes}
                  </div>
                  <div className="mt-2 space-y-1">
                    {minutes.map((minute) => {
                      const isDisabled = isTimeDisabled(
                        selectedHour || "00",
                        minute,
                      );
                      return (
                        <button
                          key={minute}
                          onClick={() =>
                            handleTimeSelect(selectedHour || "00", minute)
                          }
                          disabled={isDisabled}
                          className={`flex h-8 w-full items-center justify-center rounded-lg text-sm transition-colors ${
                            minute === selectedMinute
                              ? "bg-primary font-medium text-black"
                              : isDisabled
                                ? "cursor-not-allowed bg-black/5 text-black/30"
                                : "text-black hover:bg-black/10"
                          }`}
                          title={t.minutesTitle(minute)}
                        >
                          {minute}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
